'use client';

import { PricesCompareTableStoryblok, PricesStoryblok } from '@/storyblok/types';
import { PropsWithChildren, useMemo } from 'react';
import { blueCheck, greenCheck, roseCheck } from './checks';
import { StoryblokReactComponentProps } from '@/storyblok/components/componentsMap';

type PlanColor = 'green' | 'blue' | 'rose';

const ALL = Symbol.for('ALL');

export const StoryblokPricesCompareTable = ({
  component,
  prices,
}: StoryblokReactComponentProps<PricesCompareTableStoryblok> & { prices: PricesStoryblok }) => {
  const idxToColor: Record<number, PlanColor> = useMemo(() => {
    const idxToColor: Record<number, PlanColor> = {};
    const head = component.table?.thead;
    if (!head) return {};
    for (let i = 0; i < head.length - 1; i++) {
      const planName = head.at(i + 1)?.value;
      if (!planName) continue;
      const color = prices.tabs?.at(0)?.cards?.find(card => (card.name ?? '').toLowerCase() === planName.toLowerCase())?.color;
      if (!color) {
        idxToColor[i] = 'green';
      } else {
        idxToColor[i] = color as PlanColor;
      }
    }
    return idxToColor;
  }, [component, prices]);

  const featuresToPlans: Record<string, typeof ALL | Array<{ label: string; color: PlanColor }>> = useMemo(() => {
    const features: { featureName: string; index: number }[] =
      component.table?.tbody
        .map(row => row.body.at(0))
        .filter(Boolean)
        .map((cell, i) => ({ featureName: cell!.value ?? '', index: i })) ?? [];

    const numberOfPlans = (component.table?.thead.length ?? 0) - 1;

    if (numberOfPlans <= 0) return {};

    return features.reduce(
      (acc, { featureName, index }) => {
        const plansHavingFeature = (component.table?.tbody
          .at(index)
          ?.body.filter((_, i) => i !== 0)
          .map((cell, i) => (!!cell.value ? { label: component.table?.thead.at(i + 1)?.value ?? '', color: idxToColor[i] } : undefined))
          .filter(Boolean) ?? []) as Array<{ label: string; color: PlanColor }>;

        if (plansHavingFeature.length === 0) return acc;
        else if (plansHavingFeature.length === numberOfPlans) return { ...acc, [featureName]: ALL };
        else return { ...acc, [featureName]: plansHavingFeature };
      },
      {} as Record<string, typeof ALL | Array<{ label: string; color: PlanColor }>>,
    );
  }, [component, idxToColor]);

  return (
    <div className="mt-32">
      <h3 className="text-center text-5xl font-medium text-neutral-darker">{component.title}</h3>
      {component.content && (
        <p className="mx-auto mb-24 mt-10 text-center text-xl font-normal text-neutral-lighter lg:w-3/4">{component.content}</p>
      )}

      {component.table && (
        <>
          <div className="hidden lg:block">
            <table className="w-full">
              <thead>
                <tr>
                  {component.table.thead.map((head, i) =>
                    i === 0 ? (
                      <td key={head._uid} className="text-3xl font-semibold text-neutral-darker">
                        {head.value}
                      </td>
                    ) : (
                      <td key={head._uid} className="text-center text-2xl font-medium">
                        <ColoredText color={idxToColor[i - 1]}>{head.value}</ColoredText>
                      </td>
                    ),
                  )}
                </tr>
              </thead>
              <tbody>
                {component.table.tbody.map(row => (
                  <tr key={row._uid}>
                    {row.body.map((cell, i) =>
                      i === 0 ? (
                        <td key={cell._uid} className="h-[1px] border-b pb-5 pt-14 text-lg">
                          <div className="flex h-full flex-col justify-center">{cell.value}</div>
                        </td>
                      ) : (
                        <td key={cell._uid} className="border-b px-14 pb-5 pt-14">
                          <div className="flex flex-row justify-center">{!!cell.value ? <Check color={idxToColor[i - 1]} /> : ''}</div>
                        </td>
                      ),
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex lg:hidden">
            <ul className="flex w-full flex-col gap-5">
              {Object.entries(featuresToPlans).map(([feature, plans]) => (
                <li key={feature} className="flex flex-row justify-between border-b pb-7">
                  <span>{feature}</span>
                  <div className="flex flex-row gap-2">
                    {plans === ALL ? (
                      <MobilePlanBadge color="gray" label="Tutti i piani" />
                    ) : (
                      plans.map(plan => <MobilePlanBadge key={plan.label} color={plan.color} label={plan.label} />)
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
    </div>
  );
};

const MobilePlanBadge = ({ color, label }: { color: PlanColor | 'gray'; label: string }) => {
  switch (color) {
    case 'green':
      return <span className="rounded bg-brandGreen-100 p-2 text-brandGreen-400">{label}</span>;
    case 'blue':
      return <span className="rounded bg-brandBlue-100 p-2 text-brandBlue-500">{label}</span>;
    case 'rose':
      return <span className="rounded bg-pink-100 p-2 text-pink-500">{label}</span>;
    case 'gray':
      return <span className="rounded bg-neutral-100 p-2 text-neutral-darker">{label}</span>;
    default:
      return null;
  }
};

const Check = ({ color }: { color: PlanColor }) => {
  switch (color) {
    case 'green':
      return greenCheck;
    case 'blue':
      return blueCheck;
    case 'rose':
      return roseCheck;
    default:
      return null;
  }
};

const ColoredText = ({ color, children }: PropsWithChildren<{ color: PlanColor }>) => {
  switch (color) {
    case 'green':
      return <span className="text-brandGreen-400">{children}</span>;
    case 'blue':
      return <span className="text-brandBlue-500">{children}</span>;
    case 'rose':
      return <span className="text-rose-500">{children}</span>;
    default:
      return null;
  }
};
